import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useProviderPriceListItem, { useProviderPriceListItemForm } from '@hooks/useProviderPriceListItem'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import EntitySelector from '@components/EntitySelector'

const MODAL_KEY = 'CreateOrEditProviderPriceListItemModal'

type ModalCallbacks = {
  closeModal: () => void,
  createProviderPriceListItem: (payload: HandleCallbackActionParams) => void,
  updateProviderPriceListItem: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditProviderPriceListItemModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const {
    closeModal,
    createProviderPriceListItem,
    updateProviderPriceListItem,
  } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const providerPriceListItem = digObject(modalPayload, 'providerPriceListItem', {})

  const newRecord = !providerPriceListItem.id

  const {
    callbacks: {
      createProviderPriceListItem: createFn,
      updateProviderPriceListItem: updateFn,
    },
    creating,
    updating,
  } = useProviderPriceListItem(providerPriceListItem)

  const {
    entityState,
    entityState: {
      price,
      quantity,
      service_product_id,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useProviderPriceListItemForm(providerPriceListItem, { validateOn: 'change' })

  const createOrUpdateProviderPriceListItemPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Price List Item ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Price List Item"
      />

      <SidebarModal.Content>
        <Form>
          <EntitySelector
            callbacks={{
              selectItem: serviceProduct => setEntityState({ service_product_id: serviceProduct?.id }),
            }}
            disabled
            endpoint="service_products"
            entityKey="serviceProducts"
            errorMessage={errors.service_product_id}
            label="Service Product"
            selectedItemId={service_product_id}
          />

          <Form.Field label="* Quantity" errorMessage={errors.quantity} marginTop="large">
            <input name="quantity" type="number" value={quantity} {...handlers} />
          </Form.Field>

          <Form.Field label="* Price" errorMessage={errors.price} marginTop="large">
            <input name="price" type="number" value={price} {...handlers} />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createProviderPriceListItem(createOrUpdateProviderPriceListItemPayload)
              : () => updateProviderPriceListItem(createOrUpdateProviderPriceListItemPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Price List Item` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditProviderPriceListItemModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
